interface ResolveAutoFilledRoleParams {
  // True when editing an existing report — we never auto-fill in that case.
  isEditing: boolean;
  // The role currently held by the form (empty string when nothing is selected).
  currentRole: string;
  // The last value this logic auto-filled, or null if it never has.
  lastAutoFilledRole: string | null;
  // The user's most-recent involvement for the selected (user, product), if any.
  mostRecentRole: string | undefined;
}

/**
 * Decides what the report form's role field should hold for a new entry.
 *
 * Returns the value to set — a role, or an empty string to clear a now-stale suggestion —
 * or `null` to leave the field untouched. The rules:
 * - Never touch the field while editing an existing report.
 * - Never overwrite a role the user picked manually: a non-empty value that differs from the
 *   last value we auto-filled.
 * - Otherwise track the selected (user, product)'s most-recent involvement: apply it when one
 *   exists, and clear the field when none does — so switching to a product/user with no prior
 *   involvement doesn't leave the previous selection's role behind.
 * - Skip when the field already holds the resolved value, to avoid redundant updates.
 */
export const resolveAutoFilledRole = ({
  isEditing,
  currentRole,
  lastAutoFilledRole,
  mostRecentRole,
}: ResolveAutoFilledRoleParams): string | null => {
  if (isEditing) return null;
  const isManualSelection = currentRole !== '' && currentRole !== lastAutoFilledRole;
  if (isManualSelection) return null;
  const nextRole = mostRecentRole ?? '';
  if (currentRole === nextRole) return null;
  return nextRole;
};
